import { captureMessage } from "@sentry/browser";
import type { CognitoUserSession } from "amazon-cognito-identity-js";
import { AuthenticationDetails, CognitoUser, CognitoUserAttribute, CognitoUserPool } from "amazon-cognito-identity-js";
import Flags from "common/constants/flags";
import * as cookie from "./cookie";
import { encodeBase64Url } from "./utils/base64";
import { decodeJwtPayload, getEmailFromJwt, isJwtExpired } from "./utils/jwtClient";

export { getEmailFromJwt };

const SSO_PKCE_VERIFIER_KEY = "sso_pkce_verifier";
const SSO_PKCE_STATE_KEY = "sso_pkce_state";
export const SSO_REFRESH_TOKEN_KEY = "sso_refresh_token";

/** Backoff timestamp shared via localStorage so all tabs skip refresh attempts during an outage. */
export const SSO_REFRESH_BACKOFF_KEY = "sso_refresh_backoff_until";
const SSO_REFRESH_BACKOFF_BASE_MS = 60_000;
const SSO_REFRESH_BACKOFF_MAX_MS = 15 * 60_000;

export const HIGHARC_USER_COOKIE = "_higharc_user";
export const AUTH_ERRORS = {
  RATE_LIMIT_EXCEEDED: "LimitExceededException",
  USER_ALREADY_EXSITS: "UsernameExistsException",
  INVALID_PARAMETER: "InvalidParameterException",
  INVALID_CONFIRMATION_CODE: "CodeMismatchException",
  INVALID_PASSWORD: "InvalidPasswordException",
} as const;

export interface CognitoError extends Error {
  code: string;
}

const formatEmailAddress = (email: string): string => email.toLowerCase();

let userPool: CognitoUserPool | null = null;
function getUserPool() {
  if (!userPool) {
    userPool = new CognitoUserPool({
      UserPoolId: Flags.COGNITO_USER_POOL_ID,
      ClientId: Flags.COGNITO_CLIENT_ID,
    });
  }
  return userPool;
}

export async function signUp(
  email: string,
  password: string,
  givenName: string,
  familyName: string,
): Promise<CognitoUser> {
  const formattedEmail = formatEmailAddress(email);

  const attributes = [
    new CognitoUserAttribute({
      Name: "email",
      Value: formattedEmail,
    }),
    new CognitoUserAttribute({
      Name: "given_name",
      Value: givenName,
    }),
    new CognitoUserAttribute({
      Name: "family_name",
      Value: familyName,
    }),
  ];

  return new Promise((resolve, reject) => {
    getUserPool().signUp(formattedEmail, password, attributes, [], (err, result) => {
      if (err || !result) {
        reject(err ?? new Error("Cognito signup returned undefined"));
        return;
      }

      resolve(result.user);
    });
  });
}

export async function confirm(email: string, code: string): Promise<void> {
  const formattedEmail = formatEmailAddress(email);
  const user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export async function sendPasswordReset(email: string): Promise<void> {
  const formattedEmail = formatEmailAddress(email);
  const user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: resolve,
      onFailure: (err: Error) => reject(err),
    });
  });
}

export async function confirmPassword({
  email,
  confirmationCode,
  newPassword,
}: {
  email: string;
  confirmationCode: string;
  newPassword: string;
}) {
  const formattedEmail = formatEmailAddress(email);
  const user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.confirmPassword(confirmationCode, newPassword, {
      onSuccess: () => {
        resolve(null);
      },
      onFailure: reject,
    });
  });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  const formattedEmail = formatEmailAddress(email);
  const user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((e) => {
      const err = e as CognitoError;

      if (err) {
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

export type PasswordChallengeFunc = (newPassword: string) => Promise<any>;

/**
 * Authenticates using USER_PASSWORD_AUTH flow (non-SRP). Used as a fallback
 * when the user doesn't exist in the new pool yet and needs to be migrated
 * transparently via the UserMigration Lambda trigger. After migration the user
 * exists in the pool and all subsequent logins revert to the SRP path.
 */
export async function initiatePasswordAuth(email: string, password: string): Promise<OAuthTokenResponse> {
  const formattedEmail = formatEmailAddress(email);

  const user = new CognitoUser({ Username: formattedEmail, Pool: getUserPool() });
  user.setAuthenticationFlowType("USER_PASSWORD_AUTH");

  const authDetails = new AuthenticationDetails({ Username: formattedEmail, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const idToken = session.getIdToken();
        resolve({
          idToken: idToken.getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          email: (idToken.payload.email as string | undefined)?.toLowerCase() ?? formattedEmail,
          firstName: (idToken.payload.given_name as string) ?? "",
          lastName: (idToken.payload.family_name as string) ?? "",
        });
      },
      onFailure: reject,
      newPasswordRequired: () => {
        reject({ errorType: "NEW_PASSWORD_CHALLENGE" });
      },
    });
  });
}

export async function logIn(email: string, password: string): Promise<CognitoUserSession> {
  const formattedEmail = formatEmailAddress(email);
  const authenticationDetails = new AuthenticationDetails({
    Username: formattedEmail,
    Password: password,
  });

  const user = new CognitoUser({
    Username: formattedEmail,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    const newPasswordRequired = () => {
      reject({
        errorType: "NEW_PASSWORD_CHALLENGE",
        setNewPassword: async (newPassword: string) => {
          return new Promise((resolveInner, rejectInner) => {
            user.completeNewPasswordChallenge(
              newPassword,
              {},
              {
                onSuccess: resolveInner,
                onFailure: rejectInner,
              },
            );
          });
        },
      });
    };

    user.authenticateUser(authenticationDetails, {
      onSuccess: resolve,
      onFailure: reject,
      newPasswordRequired,
    });
  });
}

export async function getSession(user: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    if (user === null) {
      reject("You're not logged in.");
      return;
    }

    user.getSession((err, session) => {
      if (err || !session) {
        reject(err ?? new Error("getSession returned undefined"));
        return;
      }
      if (session.isValid() === false) {
        reject("Session is invalid");
      }
      resolve(session);
    });
  });
}

/**
 * Forces a refresh-token grant, unlike getSession which returns the cached
 * session until its local expiry elapses. On NotAuthorizedException the SDK
 * clears the cached user; transient errors leave it intact.
 */
export async function refreshSession(user: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const refreshToken = user.getSignInUserSession()?.getRefreshToken();
    if (!refreshToken?.getToken()) {
      reject(new Error("No refresh token available for user"));
      return;
    }

    user.refreshSession(refreshToken, (err, result) => {
      if (err || !result) {
        reject(err ?? new Error("refreshSession returned undefined"));
        return;
      }
      resolve(result);
    });
  });
}

export async function isAuthenticated() {
  const user = getUserPool().getCurrentUser();
  return user ? getSession(user) : false;
}

export function getUser() {
  return getUserPool().getCurrentUser();
}

export function logOut() {
  // Sign out of the SDK session if one exists. If no session is cached (e.g.
  // after a migration-path login where the SDK and our cookie are out of sync),
  // skip silently — the cookie removal in lib/auth.ts still completes the logout.
  getUserPool().getCurrentUser()?.signOut();
}

// --- OAuth 2.0 / PKCE helpers for SSO federation ---

/** Refresh tokens live in localStorage so SSO sessions are shared across tabs. */
function readStoredSsoRefreshToken(): string | null {
  const fromLocal = localStorage.getItem(SSO_REFRESH_TOKEN_KEY);
  if (fromLocal) {
    return fromLocal;
  }

  // Migrate sessions that still have the refresh token in sessionStorage (pre-fix).
  const fromSession = sessionStorage.getItem(SSO_REFRESH_TOKEN_KEY);
  if (fromSession) {
    localStorage.setItem(SSO_REFRESH_TOKEN_KEY, fromSession);
    sessionStorage.removeItem(SSO_REFRESH_TOKEN_KEY);
    return fromSession;
  }

  return null;
}

export function setSsoRefreshToken(refreshToken: string): void {
  localStorage.setItem(SSO_REFRESH_TOKEN_KEY, refreshToken);
  sessionStorage.removeItem(SSO_REFRESH_TOKEN_KEY);
  // A fresh token invalidates any backoff left over from the previous one.
  localStorage.removeItem(SSO_REFRESH_BACKOFF_KEY);
}

export function clearSsoRefreshToken(): void {
  localStorage.removeItem(SSO_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(SSO_REFRESH_TOKEN_KEY);
  localStorage.removeItem(SSO_REFRESH_BACKOFF_KEY);
}

/**
 * Returns a non-expired ID token from the shared auth cookie, if present.
 * Used by new tabs opened after SSO login — the Cognito SDK session and
 * sessionStorage refresh token are tab-local, but the cookie is not.
 */
export function getValidJwtFromUserCookie(): string | null {
  const userCookie = cookie.get(HIGHARC_USER_COOKIE);
  if (!userCookie) {
    return null;
  }

  try {
    const parsed = JSON.parse(userCookie) as { jwtToken?: string };
    const jwtToken = parsed.jwtToken;
    if (typeof jwtToken !== "string" || jwtToken.length === 0) {
      return null;
    }
    if (isJwtExpired(jwtToken)) {
      return null;
    }
    return jwtToken;
  } catch {
    return null;
  }
}

/**
 * Generates a PKCE verifier/challenge pair and persists the verifier in
 * sessionStorage so it survives the IdP redirect.
 */
export async function generatePKCEPair(state: string): Promise<{ verifier: string; challenge: string }> {
  const verifierBytes = crypto.getRandomValues(new Uint8Array(64));
  const verifier = encodeBase64Url(verifierBytes);

  const challengeBytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = encodeBase64Url(new Uint8Array(challengeBytes));

  sessionStorage.setItem(SSO_PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(SSO_PKCE_STATE_KEY, state);

  return { verifier, challenge };
}

export function getPKCEState(): string | null {
  return sessionStorage.getItem(SSO_PKCE_STATE_KEY);
}

export interface OAuthTokenResponse {
  idToken: string;
  refreshToken: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Exchanges the authorization code returned by Cognito Hosted UI for tokens.
 * Reads the PKCE verifier from sessionStorage and clears it after use.
 */
export async function exchangeCodeForTokens(code: string, redirectUri: string): Promise<OAuthTokenResponse> {
  const verifier = sessionStorage.getItem(SSO_PKCE_VERIFIER_KEY);
  if (!verifier) {
    throw new Error("PKCE verifier not found — SSO flow may have been initiated in a different tab or session.");
  }

  const domain = Flags.COGNITO_DOMAIN;
  const clientId = Flags.COGNITO_CLIENT_ID;

  const params = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });

  const response = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Token exchange failed: ${response.status} ${text}`);
  }

  const data = await response.json();

  sessionStorage.removeItem(SSO_PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(SSO_PKCE_STATE_KEY);

  if (data.refresh_token) {
    setSsoRefreshToken(data.refresh_token);
  }

  const payload = decodeJwtPayload(data.id_token);
  const email = typeof payload?.email === "string" ? payload.email.toLowerCase() : "";
  const firstName = typeof payload?.given_name === "string" ? payload.given_name : "";
  const lastName = typeof payload?.family_name === "string" ? payload.family_name : "";

  return {
    idToken: data.id_token,
    refreshToken: data.refresh_token ?? "",
    email,
    firstName,
    lastName,
  };
}

/**
 * Parses an HTTP `Retry-After` header (delay-seconds or HTTP-date) into a
 * millisecond delay, or null when absent or unparseable. The header is often
 * unreadable cross-origin (it needs `Access-Control-Expose-Headers`); callers
 * then fall back to the jittered default base.
 */
function parseRetryAfterMs(headerValue: string | null): number | null {
  if (!headerValue) {
    return null;
  }

  const seconds = Number(headerValue);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000;
  }

  const dateMs = Date.parse(headerValue);
  if (!Number.isNaN(dateMs)) {
    return Math.max(0, dateMs - Date.now());
  }

  return null;
}

/**
 * Persists the next allowed refresh attempt in localStorage so a sustained
 * outage does not turn every request into a token refresh (getToken() runs
 * per request and only dedupes concurrent calls). A server-sent `Retry-After`
 * is honored exactly; only the default base is jittered.
 */
function setSsoRefreshBackoff(retryAfterMs: number | null): void {
  if (retryAfterMs !== null) {
    const capped = Math.min(retryAfterMs, SSO_REFRESH_BACKOFF_MAX_MS);
    localStorage.setItem(SSO_REFRESH_BACKOFF_KEY, String(Date.now() + capped));
    return;
  }

  const jittered = SSO_REFRESH_BACKOFF_BASE_MS * (0.75 + Math.random() * 0.5);
  localStorage.setItem(SSO_REFRESH_BACKOFF_KEY, String(Date.now() + jittered));
}

/**
 * Refreshes an SSO session using the stored refresh token.
 * Returns the new ID token, or null when no refresh token is available or the
 * refresh attempt failed. The stored token is cleared only when Cognito
 * rejects the grant itself (a well-formed `invalid_grant` body) — other 4xx/5xx
 * and network failures are treated as transient and keep the token.
 */
export async function refreshSSOToken(): Promise<string | null> {
  const refreshToken = readStoredSsoRefreshToken();
  if (!refreshToken) {
    return null;
  }

  const backoffUntil = Number(localStorage.getItem(SSO_REFRESH_BACKOFF_KEY));
  if (Number.isFinite(backoffUntil) && Date.now() < backoffUntil) {
    return null;
  }
  localStorage.removeItem(SSO_REFRESH_BACKOFF_KEY);

  const domain = Flags.COGNITO_DOMAIN;
  const clientId = Flags.COGNITO_CLIENT_ID;

  const params = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: clientId,
    refresh_token: refreshToken,
  });

  try {
    const response = await fetch(`${domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    });

    if (!response.ok) {
      // A 400 is not necessarily a rejected grant: Cognito also uses it for
      // invalid_client, and intermediaries (WAF, ALB) return 4xx/5xx with
      // non-JSON bodies. A wrong wipe is the expensive direction, so only a
      // well-formed `invalid_grant` clears the token.
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      if (body?.error === "invalid_grant") {
        clearSsoRefreshToken();
        captureMessage(`SSO refresh token rejected by Cognito: ${response.status}`, "warning");
        return null;
      }

      // Transient failure — keep the token so a later refresh can succeed.
      setSsoRefreshBackoff(parseRetryAfterMs(response.headers.get("Retry-After")));
      captureMessage(`SSO refresh token request failed: ${response.status}`, "warning");
      return null;
    }

    const data = await response.json();
    if (!data.id_token) {
      // Every failure path must throttle — an unusable success would otherwise
      // bypass the backoff and be retried on every request.
      setSsoRefreshBackoff(null);
      captureMessage("SSO refresh token response did not include an id_token", "warning");
      return null;
    }
    return data.id_token;
  } catch (e) {
    // Network failure, or an intermediary error page the cross-origin response
    // never lets us read. Treat as transient and keep the token.
    setSsoRefreshBackoff(null);
    captureMessage("SSO refresh token request failed: network error", "warning");
    return null;
  }
}
